const toPositiveInt = (value) => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const roundQuantity = (value) => Number((Number(value) || 0).toFixed(5));
const roundMissingQuantity = (value) => Math.max(roundQuantity(value), 0);
const normalizeProductType = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return normalized || null;
};
const normalizeProductionKind = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return normalized === "semi_finished" ? "semi_finished" : "finished";
};
const normalizeBooleanFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";
const toPositiveQuantity = (value) => {
  const normalizedValue =
    typeof value === "string"
      ? value.replace(/\s/g, "").replace(/,/g, ".")
      : value;
  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? Number(parsedValue.toFixed(5))
    : null;
};

export const isProductionStockItem = (item) =>
  normalizeProductType(item?.product_type) === "production";

export const splitStockShortageItems = (items = []) => {
  const productionItems = [];
  const purchaseItems = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    if (isProductionStockItem(item)) {
      productionItems.push(item);
    } else {
      purchaseItems.push(item);
    }
  });

  return { productionItems, purchaseItems };
};

export const buildMissingStockItems = (materials = []) => {
  const itemsByProductId = new Map();

  (Array.isArray(materials) ? materials : []).forEach((material) => {
    const productId = toPositiveInt(
      material?.product_id ?? material?.produit_id ?? material?.id_produit
    );
    const rawRequiredQuantity =
      material?.required_quantity ??
      material?.requested_quantity ??
      material?.quantity ??
      material?.quantite;
    const rawAvailableQuantity =
      material?.available_quantity ??
      material?.available ??
      material?.current_stock ??
      material?.quantite_stock;
    const requiredQuantity = Number(rawRequiredQuantity);
    const availableQuantity = Number(rawAvailableQuantity);
    const productType = normalizeProductType(
      material?.product_type ?? material?.type_produit
    );
    const productionKind =
      productType === "production"
        ? normalizeProductionKind(material?.production_kind)
        : null;
    const productionUnitId =
      toPositiveInt(material?.production_unit_id) ||
      (productType === "production" ? productId : null);

    if (
      !productId ||
      rawRequiredQuantity === null ||
      rawRequiredQuantity === undefined ||
      rawAvailableQuantity === null ||
      rawAvailableQuantity === undefined ||
      rawAvailableQuantity === "" ||
      !Number.isFinite(requiredQuantity) ||
      !Number.isFinite(availableQuantity) ||
      requiredQuantity <= 0
    ) {
      return;
    }

    const existingItem = itemsByProductId.get(productId);
    if (existingItem) {
      existingItem.required_quantity = roundQuantity(
        existingItem.required_quantity + requiredQuantity
      );
      existingItem.available_quantity = roundQuantity(
        Math.min(existingItem.available_quantity, availableQuantity)
      );
      existingItem.product_type = existingItem.product_type || productType;
      existingItem.production_kind =
        existingItem.production_kind || productionKind;
      existingItem.production_unit_id =
        existingItem.production_unit_id || productionUnitId;
      existingItem.sellable_without_production =
        existingItem.sellable_without_production ||
        normalizeBooleanFlag(material?.sellable_without_production);
      return;
    }

    itemsByProductId.set(productId, {
      product_id: productId,
      product_name:
        material?.product_name ||
        material?.nom_produit ||
        material?.name ||
        `Produit ${productId}`,
      required_quantity: roundQuantity(requiredQuantity),
      available_quantity: roundQuantity(availableQuantity),
      purchase_price: Math.max(
        Number(
          material?.standard_purchase_price ??
            material?.purchase_price ??
            material?.estimated_purchase_price
        ) || 0,
        0
      ),
      unit_id: toPositiveInt(material?.unit_id ?? material?.unit?.id),
      unit_name: material?.unit_name || material?.unit?.name || "",
      unit_short_name:
        material?.unit_short_name || material?.unit?.short_name || "",
      product_type: productType,
      production_kind: productionKind,
      production_unit_id: productionUnitId,
      sellable_without_production: normalizeBooleanFlag(
        material?.sellable_without_production
      ),
    });
  });

  return Array.from(itemsByProductId.values())
    .map((item) => ({
      ...item,
      missing_quantity: roundMissingQuantity(
        item.required_quantity - item.available_quantity
      ),
    }))
    .filter((item) => item.missing_quantity > 0);
};

export const buildStockReplenishmentRequest = ({
  requestId = null,
  sourceType,
  sourceLabel,
  sourcePath,
  companyId,
  warehouseId,
  items,
  metadata = {},
} = {}) => ({
  ...metadata,
  request_id:
    requestId ||
    `${String(sourceType || "stock").replace(/[^a-z0-9_-]/gi, "-")}-${Date.now()}`,
  target_order_type: "purchase",
  source_type: sourceType || "stock_shortage",
  source_label: sourceLabel || "besoin de stock",
  source_path: sourcePath || "",
  company_id: toPositiveInt(companyId?.id ?? companyId),
  warehouse_id: toPositiveInt(warehouseId?.id ?? warehouseId),
  items: (Array.isArray(items) ? items : []).filter(
    (item) =>
      toPositiveInt(item?.product_id) && Number(item?.missing_quantity) > 0
  ),
});

const isInternalPath = (value) =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//");
const isProductionPath = (value) =>
  isInternalPath(value) && String(value).startsWith("/production/");

export const isProductionStockReplenishment = (replenishment) =>
  Boolean(
    replenishment &&
      (replenishment.source_type === "production" ||
        (toPositiveInt(replenishment.production_unit_id) &&
          String(replenishment.source_path || "").startsWith("/production/")))
  );

const getSalesDraftId = (replenishment) => {
  const draftId = replenishment?.return_context?.sales_draft_id;
  return typeof draftId === "string" && /^[a-z0-9-]{8,100}$/i.test(draftId)
    ? draftId
    : null;
};

export const isSalesStockReplenishment = (replenishment) =>
  Boolean(
    replenishment &&
      ["pos", "sale"].includes(replenishment.source_type) &&
      getSalesDraftId(replenishment)
  );

const normalizeSalesProductionItems = (items = []) =>
  (Array.isArray(items) ? items : [])
    .filter(isProductionStockItem)
    .map((item) => {
      const productId = toPositiveInt(item?.product_id);
      const productionUnitId =
        toPositiveInt(item?.production_unit_id) || productId;
      const outputQuantity = toPositiveQuantity(
        item?.output_quantity ?? item?.missing_quantity
      );

      if (!productId || !productionUnitId || !outputQuantity) {
        return null;
      }

      return {
        ...item,
        product_id: productId,
        product_type: "production",
        production_kind: normalizeProductionKind(item?.production_kind),
        production_unit_id: productionUnitId,
        output_quantity: outputQuantity,
        missing_quantity:
          toPositiveQuantity(item?.missing_quantity) || outputQuantity,
      };
    })
    .filter(Boolean);

export const buildSalesProductionReplenishmentRequest = ({
  requestId = null,
  sourceType,
  sourceLabel,
  sourcePath,
  userId,
  companyId,
  warehouseId,
  items,
  metadata = {},
} = {}) => ({
  ...metadata,
  request_id:
    requestId ||
    `${String(sourceType || "sale").replace(/[^a-z0-9_-]/gi, "-")}-production-${Date.now()}`,
  target_replenishment_type: "production",
  source_type: sourceType || "sale",
  source_label: sourceLabel || "vente en cours",
  source_path: sourcePath || "",
  user_id: toPositiveInt(userId?.id ?? userId),
  company_id: toPositiveInt(companyId?.id ?? companyId),
  warehouse_id: toPositiveInt(warehouseId?.id ?? warehouseId),
  current_index: 0,
  items: normalizeSalesProductionItems(items),
});

export const isSalesProductionReplenishment = (replenishment) =>
  Boolean(
    replenishment &&
      replenishment.target_replenishment_type === "production" &&
      isSalesStockReplenishment(replenishment) &&
      toPositiveInt(replenishment.user_id) &&
      toPositiveInt(replenishment.company_id) &&
      toPositiveInt(replenishment.warehouse_id) &&
      normalizeSalesProductionItems(replenishment.items).length > 0
  );

export const getSalesProductionReplenishmentStep = (replenishment) => {
  if (!isSalesProductionReplenishment(replenishment)) return null;

  const items = normalizeSalesProductionItems(replenishment.items);
  const requestedIndex = Number(replenishment.current_index);
  const currentIndex = Number.isInteger(requestedIndex) ? requestedIndex : 0;
  if (currentIndex < 0 || currentIndex >= items.length) return null;

  return {
    item: items[currentIndex],
    current_index: currentIndex,
    total: items.length,
    has_next: currentIndex + 1 < items.length,
  };
};

export const buildSalesProductionReplenishmentNavigation = (
  replenishment,
  requestedIndex = null
) => {
  if (!isSalesProductionReplenishment(replenishment)) return null;

  const items = normalizeSalesProductionItems(replenishment.items);
  const fallbackIndex = Number(replenishment.current_index);
  const parsedIndex = Number(
    requestedIndex === null || requestedIndex === undefined
      ? fallbackIndex
      : requestedIndex
  );
  const currentIndex = Number.isInteger(parsedIndex) ? parsedIndex : 0;
  const item = items[currentIndex];
  if (!item) return null;

  const nextReplenishment = {
    ...replenishment,
    current_index: currentIndex,
    items,
  };

  return {
    path: `/production/process/${item.production_unit_id}`,
    label:
      currentIndex > 0
        ? `Préparer ${item.product_name || `le produit ${item.product_id}`}`
        : `Lancer la production de ${
            item.product_name || `produit ${item.product_id}`
          }`,
    state: {
      salesProductionReplenishment: nextReplenishment,
    },
  };
};

export const isReturnableStockReplenishment = (replenishment) =>
  isProductionStockReplenishment(replenishment) ||
  isSalesStockReplenishment(replenishment);

export const buildStockReplenishmentReturnNavigation = (
  replenishment,
  method = "purchase"
) => {
  if (!isReturnableStockReplenishment(replenishment)) {
    return null;
  }

  const sourceType = String(replenishment.source_type || "");
  const normalizedMethod = ["adjustment", "production"].includes(method)
    ? method
    : "purchase";
  const completedAt = new Date().toISOString();

  if (isSalesStockReplenishment(replenishment)) {
    const isPos = sourceType === "pos";
    const fallbackPath = isPos ? "/gestion-pos" : "/gestion-ventes/ventes";
    const expectedPathPrefix = isPos ? "/gestion-pos" : "/gestion-ventes/ventes";
    const sourcePath = String(replenishment.source_path || "");
    const path =
      isInternalPath(sourcePath) && sourcePath.startsWith(expectedPathPrefix)
        ? sourcePath
        : fallbackPath;

    return {
      path,
      label: isPos ? "Retourner au POS" : "Retourner à la vente",
      state: {
        stockReplenishmentReturn: {
          request_id: replenishment.request_id || null,
          replenishment_method: normalizedMethod,
          source_type: sourceType,
          sales_draft_id: getSalesDraftId(replenishment),
          company_id: toPositiveInt(replenishment.company_id),
          warehouse_id: toPositiveInt(replenishment.warehouse_id),
          completed_at: completedAt,
        },
      },
    };
  }

  const returnContext =
    replenishment?.return_context &&
    typeof replenishment.return_context === "object"
      ? replenishment.return_context
      : {};
  const productionUnitId = toPositiveInt(
    returnContext.production_unit_id ?? replenishment.production_unit_id
  );
  const outputQuantity = toPositiveQuantity(
    returnContext.output_quantity ?? replenishment.output_quantity
  );
  const restaurantMode =
    returnContext.restaurant_mode === true ||
    String(replenishment.source_path || "").startsWith(
      "/production/kitchen/"
    );
  const fallbackPath = restaurantMode
    ? "/production/kitchen/process"
    : productionUnitId
      ? `/production/process/${productionUnitId}`
      : "/production/process";
  const path = isProductionPath(replenishment.source_path)
    ? replenishment.source_path
    : fallbackPath;
  const restaurantTicket =
    returnContext.restaurant_ticket &&
    typeof returnContext.restaurant_ticket === "object"
      ? returnContext.restaurant_ticket
      : null;
  const salesProductionReplenishment = isSalesProductionReplenishment(
    returnContext.sales_production_replenishment
  )
    ? returnContext.sales_production_replenishment
    : null;

  return {
    path,
    label: restaurantMode
      ? "Retourner en cuisine"
      : "Retourner à la production",
    state: {
      ...(restaurantTicket ? { restaurantTicket } : {}),
      ...(salesProductionReplenishment
        ? { salesProductionReplenishment }
        : {}),
      stockReplenishmentReturn: {
        request_id: replenishment.request_id || null,
        replenishment_method: normalizedMethod,
        production_unit_id: productionUnitId,
        production_unit_name: replenishment.production_unit_name || "",
        output_quantity: outputQuantity,
        restaurant_mode: restaurantMode,
        completed_at: completedAt,
      },
    },
  };
};
